import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { ArrowLeft, House, Fire, ArrowUp, ArrowDown, Minus } from "@phosphor-icons/react";
import { Leaderboard } from "./ManagerDashboard";
import { CrewReportCard } from "./DashboardFeatures";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Standalone Leaderboard page — promoted from a Wow Crew sub-tab to its own
// home-grid tile (2026-05-22). Visible to everyone so the entire team can
// glance at standings the moment they open the app. Three boards in one
// page, controlled by a top sub-tab strip:
//   1. "Your Store"        — same-store crew, the original WTD ranking
//   2. "District · Stores" — every store in the district ranked
//   3. "District · Crew"   — every crew member across the district ranked
//
// The district sub-tabs only render when the signed-in user has a
// district_id (everyone post-multi-tenant should), so legacy single-store
// deploys still get a clean one-board view.
export default function LeaderboardPage({ user, onBack, onGoHome }) {
  const [board, setBoard] = useState("store");  // store | district_stores | district_crew
  // Retail-week toggle (user ask 2026-05-23 "leaderboards need to restart
  // on Sunday … I want an option where you can see lw results"). Default
  // to current week; flip to last_week to coach against a closed scoreboard.
  const [period, setPeriod] = useState("this_week");
  const [performance, setPerformance] = useState([]);
  const [district, setDistrict] = useState(null);
  const [reportCrewId, setReportCrewId] = useState(null);
  const [loading, setLoading] = useState(true);
  // Default top-10 with one-tap reveal to show the full store roster. Keeps
  // the page scannable on mobile while still giving the SM a coaching view.
  const [showAllStore, setShowAllStore] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      // Skip while tab is hidden — battery/data saver. (2026-08-01)
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const r = await axios.get(`${API}/crew-performance`, { params: { period } });
        if (!cancelled) setPerformance(r.data || []);
      } catch (err) {
        console.error("[LeaderboardPage] same-store fetch failed", err);
      } finally { if (!cancelled) setLoading(false); }
    };
    load();
    const iv = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [period]);

  // District boards — single endpoint serves both sub-tabs so we don't
  // double the DB round-trips. Only fires when the user has a district_id.
  useEffect(() => {
    if (!user?.district_id) return undefined;
    let cancelled = false;
    const load = async () => {
      // Skip while tab is hidden — battery/data saver. (2026-08-01)
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const r = await axios.get(`${API}/districts/${user.district_id}/leaderboards`, { params: { period } });
        if (!cancelled) setDistrict(r.data || null);
      } catch (err) {
        console.error("[LeaderboardPage] district fetch failed", err);
      }
    };
    load();
    const iv = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user?.district_id, period]);

  // Personal rank call-out for the "Your Store" board.
  const meBlock = useMemo(() => {
    if (!performance.length || !user?.crew_id) return null;
    const sorted = [...performance].sort((a, b) => (b.wtd || 0) - (a.wtd || 0));
    const myIdx = sorted.findIndex((p) => p.crew_id === user.crew_id);
    if (myIdx < 0) return null;
    const me = sorted[myIdx];
    if (myIdx === 0) {
      const nextCount = sorted[1]?.wtd || 0;
      const lead = (me.wtd || 0) - nextCount;
      return {
        rank: 1,
        me,
        headline: "You're #1 this week — keep it up!",
        sub: lead > 0 ? `Leading by ${lead} email${lead === 1 ? "" : "s"}.` : "Tied at the top — don't slow down.",
        tone: "gold",
      };
    }
    const ahead = sorted[myIdx - 1];
    const diff = (ahead.wtd || 0) - (me.wtd || 0);
    return {
      rank: myIdx + 1,
      me,
      headline: `You're #${myIdx + 1}`,
      sub: diff === 0
        ? `Tied with ${ahead.first_name} — next capture takes the lead.`
        : `${diff} more email${diff === 1 ? "" : "s"} to pass ${ahead.first_name}.`,
      tone: myIdx < 3 ? "podium" : "chase",
    };
  }, [performance, user?.crew_id]);

  const toneStyles = {
    gold: "bg-gradient-to-br from-amber-300 via-yellow-400 to-amber-500 text-amber-900",
    podium: "bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 text-white",
    chase: "bg-gradient-to-br from-slate-800 via-slate-900 to-black text-white",
  };

  return (
    <div className="min-h-screen bg-neutral-100 pb-10" data-testid="crew-leaderboard-page">
      <header className="sticky top-0 z-10 bg-white/90 backdrop-blur-md border-b border-slate-200 px-4 py-3 flex items-center gap-3">
        <button onClick={onBack} className="p-1.5 rounded-full hover:bg-slate-100" aria-label="Back" data-testid="leaderboard-back">
          <ArrowLeft weight="bold" className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-base sm:text-lg font-black tracking-tight" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
            Leaderboards
          </h1>
          <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mt-0.5">
            {period === "last_week" ? "Last week · Sun → Sat" : "This week · Sun → today"}
          </p>
        </div>
        {onGoHome && (
          <button onClick={onGoHome} className="p-1.5 rounded-full hover:bg-slate-100" aria-label="Home" data-testid="leaderboard-home">
            <House weight="bold" className="w-5 h-5" />
          </button>
        )}
      </header>

      <main className="max-w-3xl mx-auto px-4 py-5 space-y-4">
        {/* Period toggle — retail-week scoreboard reset every Sunday. */}
        <div
          className="bg-slate-900 rounded-full p-1 flex items-center w-full max-w-xs mx-auto shadow-sm"
          data-testid="leaderboard-period-toggle"
        >
          {[
            { id: "this_week", label: "This Week" },
            { id: "last_week", label: "Last Week" },
          ].map((p) => {
            const active = period === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setPeriod(p.id)}
                className={`flex-1 h-8 rounded-full text-[11px] font-bold transition-all ${active ? "bg-white text-slate-900 shadow-sm" : "text-slate-300 hover:text-white"}`}
                data-testid={`leaderboard-period-${p.id}`}
                aria-pressed={active}
              >
                {p.label}
              </button>
            );
          })}
        </div>
        {user?.district_id && (
          <div
            className="bg-slate-200 rounded-full p-1 flex items-center w-full max-w-md mx-auto shadow-sm"
            data-testid="leaderboard-subtabs"
          >
            {[
              { id: "store",           label: "Your Store" },
              { id: "district_stores", label: "District · Stores" },
              { id: "district_crew",   label: "District · Crew" },
            ].map((t) => {
              const active = board === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setBoard(t.id)}
                  className={`flex-1 h-8 rounded-full text-[11px] font-bold transition-all ${active ? "bg-white shadow-sm text-[var(--fb-blue)]" : "text-slate-500 hover:text-slate-700"}`}
                  data-testid={`leaderboard-subtab-${t.id}`}
                  aria-pressed={active}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        )}

        {board === "store" && meBlock && (
          <div
            className={`fb-enter rounded-2xl p-4 shadow-lg ${toneStyles[meBlock.tone]}`}
            style={{ "--fb-delay": "0ms" }}
            data-testid="me-callout"
          >
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-14 h-14 rounded-full ring-4 ring-white/40 overflow-hidden flex items-center justify-center" style={{ backgroundColor: "rgba(255,255,255,0.2)" }}>
                {meBlock.me.photo_url ? (
                  <img src={meBlock.me.photo_url} alt={meBlock.me.first_name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xl font-black" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
                    {(meBlock.me.first_name || "?")[0].toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80">This week</p>
                <p className="text-xl sm:text-2xl font-black leading-tight" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
                  {meBlock.headline}
                </p>
                <p className="text-sm opacity-90">{meBlock.sub}</p>
              </div>
              <div className="flex-shrink-0 text-right">
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80">You</p>
                <p className="text-3xl font-black" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
                  {meBlock.me.wtd || 0}
                </p>
                <p className="text-[10px] uppercase tracking-wider font-bold opacity-80">emails</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-white/30">
              <div className="text-center">
                <p className="text-[9px] uppercase tracking-wider font-bold opacity-70">LW</p>
                <p className="text-lg font-black" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>{meBlock.me.lw || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-[9px] uppercase tracking-wider font-bold opacity-70">WTD</p>
                <p className="text-lg font-black" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>{meBlock.me.wtd || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-[9px] uppercase tracking-wider font-bold opacity-70">MTD</p>
                <p className="text-lg font-black" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>{meBlock.me.mtd || 0}</p>
              </div>
            </div>
            {typeof meBlock.me.lw === "number" && (
              <div className="mt-2 flex items-center gap-1 text-xs font-semibold opacity-90">
                {meBlock.me.wtd > meBlock.me.lw ? (
                  <><ArrowUp weight="bold" className="w-3.5 h-3.5" /> Up {meBlock.me.wtd - meBlock.me.lw} from last week</>
                ) : meBlock.me.wtd < meBlock.me.lw ? (
                  <><ArrowDown weight="bold" className="w-3.5 h-3.5" /> Down {meBlock.me.lw - meBlock.me.wtd} from last week</>
                ) : (
                  <><Minus weight="bold" className="w-3.5 h-3.5" /> Same as last week</>
                )}
              </div>
            )}
          </div>
        )}

        {board === "store" && (loading ? (
          <div className="dashboard-card text-center py-12 text-slate-400">
            <Fire weight="bold" className="w-10 h-10 mx-auto mb-2 text-slate-300 animate-pulse" />
            Loading standings…
          </div>
        ) : (
          <div>
            <Leaderboard
              performance={performance}
              onCrewClick={(id) => setReportCrewId(id)}
              managerMode={!!user?.is_manager}
              limit={showAllStore ? 0 : 10}
            />
            {performance.length > 10 && (
              <div className="mt-2 text-center">
                <button
                  type="button"
                  onClick={() => setShowAllStore((v) => !v)}
                  className="text-xs font-bold text-blue-600 hover:text-blue-700 underline underline-offset-2"
                  data-testid="leaderboard-store-toggle"
                >
                  {showAllStore
                    ? `Show top 10 (${performance.length - 10} hidden)`
                    : `Show all ${performance.length} crew →`}
                </button>
              </div>
            )}
          </div>
        ))}

        {board === "district_stores" && (
          <DistrictStoresBoard data={district} user={user} period={period} />
        )}

        {board === "district_crew" && (
          <DistrictCrewBoard data={district} user={user} period={period} onCrewClick={(id) => setReportCrewId(id)} />
        )}
      </main>

      {user?.is_manager && reportCrewId && (
        <CrewReportCard
          crewId={reportCrewId}
          onClose={() => setReportCrewId(null)}
        />
      )}
    </div>
  );
}

// --- District boards (moved 2026-05-22 from TeamPage.js when the
// Leaderboard sub-tab was promoted to a top-level home tile) ----------------
function DistrictStoresBoard({ data, user, period }) {
  if (!data) {
    return (
      <div className="dashboard-card text-center py-12 text-slate-400" data-testid="district-stores-loading">
        <Fire weight="bold" className="w-10 h-10 mx-auto mb-2 text-slate-300 animate-pulse" />
        Loading district…
      </div>
    );
  }
  const stores = data.stores || [];
  if (!stores.length) {
    return (
      <div className="dashboard-card text-center py-12 text-slate-500" data-testid="district-stores-empty">
        No district stores yet.
      </div>
    );
  }
  const max = Math.max(...stores.map((s) => s.emails), 1);
  return (
    <div className="space-y-2" data-testid="district-stores-board">
      <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500 px-1">
        {period === "last_week" ? "Last week · Sun → Sat" : "This week · Sun → today"} · emails captured · {stores.length} stores
      </p>
      {stores.map((s, i) => {
        const isMine = s.id === user?.home_store_id;
        const widthPct = (s.emails / max) * 100;
        return (
          <div
            key={s.id}
            className={`dashboard-card relative overflow-hidden ${isMine ? "ring-2 ring-[var(--fb-blue)]" : ""}`}
            data-testid={`district-stores-row-${s.code}`}
          >
            <div
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-100 to-transparent pointer-events-none"
              style={{ width: `${widthPct}%`, opacity: 0.55 }}
            />
            <div className="relative flex items-center gap-3">
              <span className={`w-7 text-center text-base font-black ${i === 0 ? "text-amber-500" : i === 1 ? "text-slate-500" : i === 2 ? "text-orange-600" : "text-slate-400"}`}>
                {i + 1}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-black truncate" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
                  {s.code} · {s.name}
                  {isMine && <span className="ml-2 text-[10px] uppercase tracking-wider bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-bold">You</span>}
                </p>
                <p className="text-[11px] text-slate-500 truncate">
                  {s.transactions.toLocaleString()} txns · {(s.capture_rate * 100).toFixed(1)}% capture
                </p>
              </div>
              <p className="text-2xl font-black tabular-nums flex-shrink-0" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
                {s.emails.toLocaleString()}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DistrictCrewBoard({ data, user, period, onCrewClick }) {
  if (!data) {
    return (
      <div className="dashboard-card text-center py-12 text-slate-400" data-testid="district-crew-loading">
        <Fire weight="bold" className="w-10 h-10 mx-auto mb-2 text-slate-300 animate-pulse" />
        Loading district…
      </div>
    );
  }
  // Cap to top 20 across the district. The full roster can be 200+ when
  // every store reports; crew below #20 effectively can't catch up by
  // week's end. Keeps the page scannable on mobile.
  const TOP_N = 20;
  const fullCrew = data.crew || [];
  const crew = fullCrew.slice(0, TOP_N);
  if (!crew.length) {
    return (
      <div className="dashboard-card text-center py-12 text-slate-500" data-testid="district-crew-empty">
        No emails captured across the district yet this week.
      </div>
    );
  }
  return (
    <div className="space-y-1" data-testid="district-crew-board">
      <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500 px-1">
        Top {crew.length} · {period === "last_week" ? "last week" : "this week"}
        {fullCrew.length > crew.length && (
          <span className="text-slate-400 normal-case font-semibold"> · {fullCrew.length - crew.length} more not shown</span>
        )}
      </p>
      <div className="dashboard-card divide-y divide-slate-100">
        {crew.map((c, i) => {
          const isMe = c.crew_id === user?.crew_id;
          return (
            <button
              key={c.crew_id}
              type="button"
              onClick={() => onCrewClick && onCrewClick(c.crew_id)}
              className={`w-full flex items-center gap-3 py-2.5 first:pt-1 last:pb-1 text-left hover:bg-slate-50 -mx-3 px-3 transition-colors ${isMe ? "bg-blue-50" : ""}`}
              data-testid={`district-crew-row-${c.crew_id}`}
            >
              <span className={`w-6 text-center text-sm font-black ${i === 0 ? "text-amber-500" : i === 1 ? "text-slate-500" : i === 2 ? "text-orange-600" : "text-slate-400"}`}>
                {i + 1}
              </span>
              {c.photo_url ? (
                <img src={c.photo_url} alt={c.first_name} className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600 flex-shrink-0">
                  {(c.first_name || "?")[0].toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold truncate">
                  {c.first_name}
                  {isMe && <span className="ml-2 text-[10px] uppercase tracking-wider bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-bold">You</span>}
                </p>
                <p className="text-[11px] text-slate-500 truncate">Store {c.store_code}</p>
              </div>
              <p className="text-base font-black text-slate-900 flex-shrink-0 tabular-nums" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
                {c.total_emails.toLocaleString()}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
